import React from 'react';

import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {CheckboxBlock, RadioBlock} from '@udemy/react-form-components';
import {safelySetInnerHTML} from '@udemy/shared-utils';

import styles from '../pop-quiz-modal.module.less';
import {PopQuizItemOption} from './types';

interface PopQuizOptionProps {
    option: PopQuizItemOption;
    questionId: string;
    isMultiSelect: boolean;
    isSelected: boolean;
    isChecked: boolean;
    correctOptionIds: string[];
    onSelect: (id: string) => void;
}

export function PopQuizOption({
    option,
    questionId,
    isMultiSelect,
    isSelected,
    isChecked,
    correctOptionIds,
    onSelect,
}: PopQuizOptionProps) {
    const {gettext} = useI18n();

    const isCorrectAnswer = isChecked && correctOptionIds.includes(option.id);
    const showFeedback = isChecked && (isSelected || isCorrectAnswer);
    const blockClassName = isChecked
        ? showFeedback
            ? isCorrectAnswer
                ? styles['feedback-correct']
                : styles['feedback-incorrect']
            : styles['feedback-dimmed']
        : undefined;
    const borderClassName = showFeedback
        ? isCorrectAnswer
            ? styles['border-correct']
            : styles['border-incorrect']
        : undefined;

    const optionText = (
        <span
            className="ud-heading-md"
            {...safelySetInnerHTML({
                descriptionOfCaller: 'pop-quiz:option-text',
                html: option.text,
            })}
        />
    );

    const labelContent = isChecked ? (
        <span className={styles['option-label-row']}>
            {optionText}
            {showFeedback && (
                <span className={styles['feedback-badge']}>
                    {isCorrectAnswer ? gettext('Correct') : gettext('Try again')}
                    <Icon
                        glyph={isCorrectAnswer ? 'success' : 'close'}
                        label={false}
                        color={isCorrectAnswer ? 'positive' : 'negative'}
                        size="small"
                    />
                </span>
            )}
        </span>
    ) : (
        optionText
    );

    if (isMultiSelect) {
        return (
            <CheckboxBlock
                name={`pop-quiz-answer-${questionId}`}
                value={option.id}
                checked={isSelected}
                disabled={isChecked}
                onChange={isChecked ? undefined : () => onSelect(option.id)}
                className={blockClassName}
                xBorderClassName={borderClassName}
                xLabelContent={labelContent}
            />
        );
    }

    return (
        <RadioBlock
            name="pop-quiz-answer"
            value={option.id}
            checked={isSelected}
            disabled={isChecked}
            onChange={isChecked ? undefined : () => onSelect(option.id)}
            className={blockClassName}
            xBorderClassName={borderClassName}
            xLabelContent={labelContent}
        />
    );
}
